import {useEffect, useState} from 'react';
import './task-card.scss';
import {Box, SxProps, Typography, Theme, Grid, Chip} from '@mui/material';
import {CardCollapse} from '../../shared/card-collapse';
import {CareTaskComplementary} from '../../../backend/interfaces/care-task-complementary';
import {CarePlanConfigurationFeature} from '../../../backend/enums/care-plan-configuration-feature';
import {CarePlanConfigurationType} from '../../../backend/enums/care-plan-configuration-type';
import {ActivitiesGroup} from '../../../backend/interfaces/activities-group';
import {EntityUser} from '../../../backend/responses/entity-user';
import {InteractionEvent} from '../pressable';
import {ConfigurationsIndex, ConfigurationsValuesIndex} from '../../../types';
import {CarePlanConfiguration} from '../../../backend/interfaces/care-plan-configuration';
import {useTranslation} from 'react-i18next';
import {translations} from '../../../i18n/translations';
import MedicineCardValue from '../task-card-value/medicine-card-value';
import {TaskCardValue} from '../task-card-value/task-card-value';
import {PosologyScheduleCardValue} from '../task-card-value/posology-schedule-card-value';
import {CardSkeleton, HeaderSkeleton} from './card-skeleton';
import {CarePlanConfigurationFieldLayoutConfigurations} from '../../../backend/interfaces/care-plan-configuration-layout';
import TaskCardHeader from './task-card-header';
import {Tooltip} from '../../tooltip/tooltip';
import {AutonomyChip} from '../../shared/badges/autonomy-chip';
import {ActivityType} from '../../../backend/interfaces/activity-type';

const featuresToIgnore = [
  CarePlanConfigurationFeature.EntityShifts,
  CarePlanConfigurationFeature.ActivationState,
  CarePlanConfigurationFeature.StartsAtDate,
  CarePlanConfigurationFeature.StartsAtTime,
  CarePlanConfigurationFeature.Name,
  CarePlanConfigurationFeature.UserContacts,
  CarePlanConfigurationFeature.MedicineBox,
  CarePlanConfigurationFeature.MedicineSOS,
];
interface TaskCardProps {
  displayType: 'user' | 'task';
  senior?: EntityUser;
  avatarBorderColor?: string;
  hideAvatar?: boolean;
  taskDetails?: CareTaskComplementary;
  activitiesGroup?: ActivitiesGroup;
  configurationsIndex?: ConfigurationsIndex;
  configurationsValuesIndex?: ConfigurationsValuesIndex;
  taskConfigurationsValuesIndex?: ConfigurationsValuesIndex;
  taskConfigurations?: Array<CarePlanConfiguration>;
  defaultExpand?: boolean;
  taskInformation?: boolean; // displayed on CareAndActivities page
  types?: ActivityType;
  onChange?: (expanded: boolean) => void;
  onClick?: (event: InteractionEvent) => void;
  onLongPress?: (event: InteractionEvent) => void;
  onSeniorAction?: (senior: EntityUser) => void;
  seniorActionsOpen?: boolean;
  seniorActionsDisabled?: boolean;
}

export default function TaskCard({
  displayType,
  senior,
  avatarBorderColor,
  hideAvatar,
  taskDetails,
  activitiesGroup,
  configurationsIndex,
  configurationsValuesIndex,
  taskConfigurationsValuesIndex,
  taskConfigurations,
  defaultExpand = false,
  taskInformation = false,
  types,
  onChange,
  onClick,
  onLongPress,
  onSeniorAction,
  seniorActionsOpen,
  seniorActionsDisabled,
}: TaskCardProps) {
  const {t} = useTranslation();
  const [taskConfig, setTaskConfig] = useState<
    Array<CarePlanConfigurationFieldLayoutConfigurations>
  >([]);
  const [activityConfigurations, setActivityConfigurations] = useState<
    CarePlanConfigurationFieldLayoutConfigurations[]
  >([]);
  const [box, setBox] = useState<boolean>(false);
  const [sos, setSos] = useState<boolean>(false);
  const [isExpanded, setIsExpanded] = useState<boolean>(defaultExpand);
  const collapseBoxWrapper: SxProps<Theme> = {
    padding: '0 4px',
  };
  useEffect(() => {
    if (!taskConfigurationsValuesIndex || !taskConfigurations || !types) return;
    let newTaskConfigurationsValues: Array<CarePlanConfigurationFieldLayoutConfigurations> = [];

    types?.carePlanConfigurationLayout?.configurations?.forEach((configuration) => {
      const field = taskConfigurations.find(
        (config) => config.id === configuration.carePlanConfigurationId
      );
      const value =
        taskConfigurationsValuesIndex![configuration.carePlanConfigurationId]
          ?.carePlanConfigurationValue;

      if (value == null || value === '' || !field) {
        // returns if carePlanConfigurationValue is undefined, null or "" but pass if is false
        return;
      }

      // Skip the specified types/feature fields
      if (
        (field.feature && featuresToIgnore.includes(field.feature)) ||
        // skip "Registo obrigatório"
        (!field.feature && field.type === CarePlanConfigurationType.Boolean)
      ) {
        if (field.feature === CarePlanConfigurationFeature.MedicineBox && value) setBox(true);
        if (field.feature === CarePlanConfigurationFeature.MedicineSOS && value) setSos(true);
        return;
      }

      if (
        field.type === CarePlanConfigurationType.ActivityTypeSchedule &&
        Object.keys(JSON.parse(value)).length === 0
      )
        return;

      newTaskConfigurationsValues.push(configuration);
    });
    setTaskConfig(newTaskConfigurationsValues);
  }, [taskConfigurationsValuesIndex, taskConfigurations, types]);

  useEffect(() => {
    if (!activitiesGroup || !configurationsIndex || !configurationsValuesIndex) return;
    let newActivityConfigurations: CarePlanConfigurationFieldLayoutConfigurations[] = [];
    activitiesGroup.carePlanConfigurationLayout?.configurations?.forEach((configuration) => {
      const field = configurationsIndex![configuration.carePlanConfigurationId];
      const value =
        configurationsValuesIndex![configuration.carePlanConfigurationId]
          ?.carePlanConfigurationValue;
      if (
        value == null ||
        value === '' ||
        !field ||
        field.type === CarePlanConfigurationType.EmptySpace
      )
        return;

      // Skip rendering the specified types/feature fields
      if (
        (field.type === CarePlanConfigurationType.ActivitiesGroupSchedule &&
          JSON.parse(value).type === 'activity-type') ||
        field?.feature === CarePlanConfigurationFeature.AutonomyAssessment
      ) {
        return;
      }

      newActivityConfigurations.push(configuration);
    });
    setActivityConfigurations(newActivityConfigurations);
  }, [activitiesGroup, configurationsIndex, configurationsValuesIndex]);

  const onCollapsibleChange = (expanded: boolean) => {
    setIsExpanded(expanded);

    onChange && onChange(expanded);
  };

  if (!taskDetails || (!senior && !taskInformation)) {
    return <HeaderSkeleton defaultExpand={defaultExpand} />;
  }

  return (
    <CardCollapse
      key="card_collapse"
      collapsableHeader={
        <TaskCardHeader
          displayType={displayType}
          senior={senior}
          taskDetails={taskDetails}
          sos={sos}
          box={box}
          taskInformationHeader={taskInformation}
          avatarBorderColor={avatarBorderColor}
          isExpanded={isExpanded}
          hideAvatar={hideAvatar}
          onClick={onClick}
          onLongPress={onLongPress}
          onSeniorAction={onSeniorAction}
          seniorActionsOpen={seniorActionsOpen}
          seniorActionsDisabled={seniorActionsDisabled}
        />
      }
      cardProps={{
        className: taskInformation ? 'task-details-info-card' : 'task-details-card',
      }}
      onChange={onCollapsibleChange}
      defaultExpand={defaultExpand}
      disabled={!taskInformation && taskDetails.careTask.markedAsHandled}
    >
      {taskInformation && taskDetails.autonomyAssessmentConfiguration && (
        <Tooltip title={taskDetails.autonomyAssessmentConfiguration?.description}>
          <AutonomyChip task={taskDetails} />
        </Tooltip>
      )}
      {taskInformation && (
        <Grid container direction="row">
          {box && isExpanded && <Chip className="box-chip" label="BOX" />}
          {sos && isExpanded && <Chip className="sos-chip" label="SOS" />}
        </Grid>
      )}
      <Box sx={collapseBoxWrapper}>
        {!activitiesGroup &&
          !configurationsIndex &&
          !configurationsValuesIndex &&
          !taskConfigurationsValuesIndex &&
          !taskConfigurations && <CardSkeleton />}

        {configurationsIndex && configurationsValuesIndex && (
          <Box
            className="task-details-card-content"
            sx={{
              layoutColumnsCount:
                activitiesGroup?.carePlanConfigurationLayout?.desktopColumnCount || 2,
            }}
          >
            {activityConfigurations.map((configuration, index) => {
              const field = configurationsIndex![configuration.carePlanConfigurationId];
              const value =
                configurationsValuesIndex![configuration.carePlanConfigurationId]
                  ?.carePlanConfigurationValue;

              return (
                <Box
                  key={`configuration-${field.id}-${index}`}
                  sx={{
                    gridColumn: `span ${configuration.columnsSpan ?? 1}`,
                  }}
                >
                  <TaskCardValue configuration={field} value={value} />
                </Box>
              );
            })}
            {taskConfig.length > 0 && (
              <Box
                sx={{
                  marginTop: activityConfigurations.length > 0 ? '4px' : '0',
                  marginBottom: '10px',
                  gridColumn: `span 2`,
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 500,
                    fontSize: '14px',
                    lineHeight: '24px',
                    color: 'var(--dcm-blue-base)',
                  }}
                >
                  {t(translations.care_and_activities.handle_task_modal.card_collapse.title)}
                </Typography>
              </Box>
            )}
            {taskConfigurations &&
              taskConfigurationsValuesIndex &&
              taskConfig.map((configuration, index) => {
                const field = taskConfigurations.find(
                  (config) => config.id === configuration.carePlanConfigurationId
                );
                if (!field) return null;
                const value = taskConfigurationsValuesIndex[field?.id];

                return (
                  <Box
                    key={`configuration-task-${field.id}-${index}`}
                    sx={{
                      gridColumn: `span ${configuration.columnsSpan ?? 1}`,
                    }}
                  >
                    {field.feature === CarePlanConfigurationFeature.Medicine ||
                    field.feature === CarePlanConfigurationFeature.MedicineAdministrationRoute ? (
                      <MedicineCardValue
                        activitiesSectorId={taskDetails?.careTask.activitiesSectorId}
                        activitiesGroupId={taskDetails?.careTask.activitiesGroupId}
                        activityTypeId={taskDetails?.careTask.activityTypeId}
                        configuration={field}
                        itemId={value?.carePlanConfigurationValue}
                      />
                    ) : field.type === CarePlanConfigurationType.PosologySchedule ? (
                      <PosologyScheduleCardValue
                        activitiesSectorId={taskDetails?.careTask.activitiesSectorId}
                        activitiesGroupId={taskDetails?.careTask.activitiesGroupId}
                        configuration={field}
                        itemId={value?.carePlanConfigurationValue}
                      />
                    ) : (
                      <TaskCardValue
                        configuration={field}
                        value={value?.carePlanConfigurationValue}
                      />
                    )}
                  </Box>
                );
              })}
          </Box>
        )}
      </Box>
    </CardCollapse>
  );
}
